// OptionsStrategyPanel.jsx
// A modal that opens when an option row in PositionLedger is clicked.
//
// Props:
//   show          – boolean
//   onHide        – () => void
//   position      – formatted option position from OrderLedgerBuilder.formatPositionForTable()
//                   Fields: symbol, status, direction, quantity, option_type, strike, expiration,
//                           avg_entry_price, entry_fill_price, entry_filled_at, entry_submitted_at,
//                           entry_submitted_at_raw, entry_order_type, entry_limit_price, entry_slippage,
//                           exit_price, exit_fill_price, exit_filled_at, exit_order_type, exit_limit_price,
//                           exit_slippage, total_pnl, total_pnl_num, tp_limit, sl_stop, sl_limit, rr,
//                           hold_time, first_entry_time
//   matchedEval   – the PROCEED liveBars entry whose timestamp is closest to and before this
//                   position's entry. Fields: option_strategy, hedge_bias, hedge_size_pct,
//                   options_notes, confidence, confidence_bucket, regime_classification,
//                   volatility_feel, rr_preview, eval_new_target, eval_new_stop_loss, t

import React, { useState } from 'react';

// ─────────────────────────────────────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────────────────────────────────────
const v    = (x, fb = '—') => (x !== undefined && x !== null && x !== '' ? x : fb);
const fmtP = (x) => { const n = parseFloat(x); return isNaN(n) || n === 0 ? '—' : `$${n.toFixed(2)}`; };
const fmtN = (x, d = 2)   => { const n = parseFloat(x); return isNaN(n) ? '—' : n.toFixed(d); };

// ─────────────────────────────────────────────────────────────────────────────
// TOKENS  (matches TradingCommand dark theme)
// ─────────────────────────────────────────────────────────────────────────────
const T = {
  bg:      '#0a0a0a',
  card:    '#111',
  surface: 'rgba(255,255,255,0.03)',
  border:  '#1e1e1e',
  border2: '#2a2a2a',
  text:    '#e2e8f0',
  muted:   '#64748b',
  subtle:  '#334155',
  green:   '#10b981', greenBg: 'rgba(16,185,129,0.1)',  greenBdr: 'rgba(16,185,129,0.25)',
  amber:   '#f59e0b', amberBg: 'rgba(245,158,11,0.1)',  amberBdr: 'rgba(245,158,11,0.25)',
  blue:    '#60a5fa', blueBg:  'rgba(96,165,250,0.1)',  blueBdr:  'rgba(96,165,250,0.25)',
  purple:  '#a78bfa', purpleBg:'rgba(167,139,250,0.1)', purpleBdr:'rgba(167,139,250,0.25)',
  red:     '#f87171', redBg:   'rgba(248,113,113,0.1)', redBdr:   'rgba(248,113,113,0.25)',
  slate:   '#94a3b8', slateBg: 'rgba(148,163,184,0.07)',slateBdr: 'rgba(148,163,184,0.18)',
};

const STRAT = {
  LONG_PREMIUM: { c: T.blue,   bg: T.blueBg,   b: T.blueBdr   },
  DEBIT_SPREAD: { c: T.purple, bg: T.purpleBg,  b: T.purpleBdr },
  SKIP_OPTIONS: { c: T.slate,  bg: T.slateBg,   b: T.slateBdr  },
};
const stratStyle = (s) => STRAT[s] || { c: T.muted, bg: T.slateBg, b: T.slateBdr };

// ─────────────────────────────────────────────────────────────────────────────
// PRIMITIVES
// ─────────────────────────────────────────────────────────────────────────────
const Chip = ({ children, c = T.muted, bg = T.slateBg, b = T.slateBdr }) => (
  <span style={{
    display: 'inline-flex', alignItems: 'center', padding: '3px 10px',
    borderRadius: '9999px', border: `1px solid ${b}`,
    backgroundColor: bg, color: c,
    fontSize: '11px', fontWeight: 600, whiteSpace: 'nowrap',
  }}>{children}</span>
);

const PnLColor = (num) => parseFloat(num) >= 0 ? T.green : T.red;

const FieldRow = ({ label, value, accent, mono }) => (
  <div style={{
    display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
    gap: '8px', padding: '5px 0', borderBottom: `1px solid ${T.border}`,
  }}>
    <span style={{ fontSize: '11px', color: T.muted, flexShrink: 0 }}>{label}</span>
    <span style={{
      fontSize: '11px', color: accent || T.text, fontWeight: accent ? 600 : 400,
      textAlign: 'right', fontFamily: mono ? 'monospace' : 'inherit',
    }}>{v(value)}</span>
  </div>
);

const SLabel = ({ children }) => (
  <div style={{
    fontSize: '10px', color: T.subtle, textTransform: 'uppercase',
    letterSpacing: '0.5px', marginBottom: '8px', marginTop: '16px',
  }}>{children}</div>
);

const Box = ({ children, style }) => (
  <div style={{
    borderRadius: '6px', border: `1px solid ${T.border}`,
    backgroundColor: T.surface, padding: '10px 12px', ...style,
  }}>{children}</div>
);

const Stat = ({ label, value, accent }) => (
  <Box>
    <div style={{ fontSize: '10px', color: T.muted, marginBottom: '3px' }}>{label}</div>
    <div style={{ fontSize: '13px', fontWeight: 700, color: accent || T.text }}>{v(value)}</div>
  </Box>
);

const TabBtn = ({ label, active, onClick }) => (
  <button onClick={onClick} style={{
    padding: '7px 16px', fontSize: '11px', cursor: 'pointer', border: 'none',
    fontWeight: active ? 600 : 400, color: active ? T.text : T.muted,
    backgroundColor: 'transparent',
    borderBottom: active ? `2px solid ${T.blue}` : '2px solid transparent',
  }}>{label}</button>
);

// ─────────────────────────────────────────────────────────────────────────────
// TAB: CONTEXT  (eval fields — why this option was chosen)
// ─────────────────────────────────────────────────────────────────────────────
// Parse volatility_feel and rr_preview out of the reasoning string
// e.g. "...volatility_feel=VERY_LOW, chase_risk=LOW...rr_preview=0.09..."
const parseReasoningField = (reasoning, field) => {
  if (!reasoning) return null;
  const match = reasoning.match(new RegExp(`${field}=([^,\\.\\s]+)`));
  return match ? match[1] : null;
};

const ContextTab = ({ position, matchedEval }) => {
  // Option fields live in eval_agent_story.chart_analysis.llm_full_response
  const story  = matchedEval?.eval_agent_story || {};
  const llm    = story.chart_analysis?.llm_full_response || {};
  const dec    = story.decision || {};  // post-adjustment confidence, target, stop

  const strat      = llm.option_strategy;
  const bias       = llm.hedge_bias;
  const sc         = stratStyle(strat);
  const optNotes   = llm.options_notes;
  const regime     = llm.regime_analysis?.regime_classification;

  // volatility_feel and rr_preview are embedded in the reasoning string only
  const reasoning  = llm.reasoning || dec.rationale_lines?.[0] || '';
  const volatility = parseReasoningField(reasoning, 'volatility_feel');
  const rrPreview  = parseReasoningField(reasoning, 'rr_preview');

  // Use decision block for post-risk-adjusted confidence, target, stop
  const confidence = dec.confidence  ?? llm.confidence;
  const confBucket = dec.confidence_bucket ?? '';
  const target     = dec.new_target   || llm.target_price;
  const stopLoss   = dec.new_stop_loss;

  if (!matchedEval) {
    return (
      <div style={{ padding: '24px', textAlign: 'center', color: T.subtle, fontSize: '12px' }}>
        No PROCEED evaluation found before this position's entry time.
        <div style={{ marginTop: '8px', fontSize: '11px', color: T.muted }}>
          Entry at: {v(position.entry_submitted_at)}
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Strategy + Hedge chips */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '12px' }}>
        <Chip c={sc.c} bg={sc.bg} b={sc.b}>STRAT: {v(strat, 'UNKNOWN')}</Chip>
        <Chip
          c={bias && bias !== 'NONE' ? T.amber : T.muted}
          bg={bias && bias !== 'NONE' ? T.amberBg : T.slateBg}
          b={bias && bias !== 'NONE' ? T.amberBdr : T.slateBdr}
        >
          HEDGE: {v(bias, 'NONE')}
          {bias && bias !== 'NONE' && llm.hedge_size_pct != null
            ? ` (${Math.round(llm.hedge_size_pct * 100)}%)` : ''}
        </Chip>
      </div>

      {/* AI Rationale */}
      {optNotes && (
        <Box style={{ marginBottom: '14px' }}>
          <div style={{ fontSize: '10px', color: T.muted, marginBottom: '5px' }}>AI Rationale</div>
          <div style={{ fontSize: '12px', color: T.text, lineHeight: '1.6' }}>
            {optNotes}
          </div>
        </Box>
      )}

      {/* Market context grid */}
      <SLabel>Market Context at Entry</SLabel>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px', marginBottom: '12px' }}>
        <Stat label="Regime"     value={v(regime)} />
        <Stat label="Volatility" value={v(volatility)} />
        <Stat label="Confidence"
          value={confidence != null
            ? `${confidence}${confBucket ? ` (${confBucket})` : ''}`
            : '—'}
        />
        <Stat label="R:R Preview"
          value={rrPreview != null ? fmtN(rrPreview) : '—'}
        />
        <Stat label="AI Target"
          value={target ? fmtP(target) : '—'}
          accent={T.green}
        />
        <Stat label="AI Stop"
          value={stopLoss ? fmtP(stopLoss) : '—'}
          accent={T.red}
        />
      </div>

      {/* Timestamp note */}
      <div style={{ fontSize: '10px', color: T.subtle, textAlign: 'right' }}>
        Eval fired: {v(matchedEval?.t ? new Date(matchedEval.t).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false }) : null)}
        {' '}· Entry submitted: {v(position.entry_submitted_at)}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// TAB: EXECUTION  (position order fields)
// ─────────────────────────────────────────────────────────────────────────────
const ExecutionTab = ({ position }) => {
  const isClosed  = position.status === 'closed';
  const pnlNum    = parseFloat(position.total_pnl_num || 0);
  const slipColor = (v) => parseFloat(v || 0) > 0 ? T.red : T.green;

  return (
    <div>
      <SLabel>Entry</SLabel>
      <FieldRow label="Submitted"  value={position.entry_submitted_at} />
      <FieldRow label="Fill time"  value={position.entry_filled_at} />
      <FieldRow label="Order type" value={v(position.entry_order_type)} />
      <FieldRow label="Limit"      value={fmtP(position.entry_limit_price)} />
      <FieldRow label="Fill price" value={fmtP(position.entry_fill_price || position.avg_entry_price)} accent={T.blue} />
      <FieldRow label="Slippage"   value={fmtN(position.entry_slippage, 4)} accent={slipColor(position.entry_slippage)} />
      <FieldRow label="Qty"        value={v(position.quantity)} />

      {isClosed && (
        <>
          <SLabel>Exit</SLabel>
          <FieldRow label="Fill time"  value={v(position.exit_filled_at)} />
          <FieldRow label="Order type" value={v(position.exit_order_type)} />
          <FieldRow label="Limit"      value={fmtP(position.exit_limit_price)} />
          <FieldRow label="Fill price" value={fmtP(position.exit_fill_price || position.exit_price)} accent={T.amber} />
          <FieldRow label="Slippage"   value={fmtN(position.exit_slippage, 4)} accent={slipColor(position.exit_slippage)} />
        </>
      )}

      <SLabel>Result</SLabel>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px', marginBottom: '12px' }}>
        <Stat label="P&L"      value={`$${fmtN(position.total_pnl_num)}`} accent={PnLColor(pnlNum)} />
        <Stat label="Hold time" value={v(position.hold_time)} />
        <Stat label="R:R"      value={fmtN(position.rr, 3)} />
      </div>

      {(position.tp_limit || position.sl_stop) && (
        <>
          <SLabel>Bracket</SLabel>
          <FieldRow label="Take profit" value={fmtP(position.tp_limit)} accent={T.green} />
          <FieldRow label="Stop loss"   value={fmtP(position.sl_stop)}  accent={T.red} />
          {position.sl_limit && <FieldRow label="Stop limit" value={fmtP(position.sl_limit)} accent={T.red} />}
        </>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// TAB: MANAGEMENT  (rules derived from strategy + hedge_bias)
// ─────────────────────────────────────────────────────────────────────────────
const ManagementTab = ({ matchedEval }) => {
  const llm      = matchedEval?.eval_agent_story?.chart_analysis?.llm_full_response || {};
  const strat    = llm.option_strategy;
  const bias     = llm.hedge_bias || 'NONE';
  const hedgePct = llm.hedge_size_pct || 0;

  const rules = [
    { label: 'Compression Lockout', on: strat === 'DEBIT_SPREAD',                         ac: T.green  },
    { label: 'No-Expansion Exit',   on: strat === 'DEBIT_SPREAD' || bias === 'REDUCE_SIZE', ac: T.amber  },
    { label: 'Convert to Spread',   on: bias === 'CONVERT_TO_SPREAD',                       ac: T.purple },
    { label: 'Reduce Size',         on: bias === 'REDUCE_SIZE',                             ac: T.amber  },
  ];

  const pyramidNote = () => {
    if (!strat)                      return 'No strategy data — pyramiding behavior undetermined.';
    if (strat === 'SKIP_OPTIONS')    return 'No options active.';
    if (strat === 'DEBIT_SPREAD')    return 'DEBIT_SPREAD: pyramid adds disabled. Spreads cap upside; adding size increases leg risk.';
    if (strat === 'LONG_PREMIUM') {
      if (bias === 'REDUCE_SIZE')    return `LONG_PREMIUM + REDUCE_SIZE: adds scaled to ${Math.round((1 - hedgePct) * 100)}% of normal size.`;
      if (bias === 'CONVERT_TO_SPREAD') return 'LONG_PREMIUM: adds disabled after spread conversion.';
      return 'LONG_PREMIUM: pyramiding active — adds allowed per confidence and regime thresholds.';
    }
    return 'Standard threshold-based pyramiding rules apply.';
  };

  if (!matchedEval) {
    return (
      <div style={{ padding: '24px', textAlign: 'center', color: T.subtle, fontSize: '12px' }}>
        No eval context — management rules cannot be determined.
      </div>
    );
  }

  return (
    <div>
      <SLabel>Active Rules</SLabel>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '16px' }}>
        {rules.map(r => (
          <div key={r.label} style={{
            display: 'flex', alignItems: 'center', gap: '10px',
            padding: '8px 12px', borderRadius: '6px',
            border: `1px solid ${r.on ? r.ac + '40' : T.border}`,
            backgroundColor: r.on ? r.ac + '0d' : 'transparent',
          }}>
            <div style={{
              width: '7px', height: '7px', borderRadius: '50%', flexShrink: 0,
              backgroundColor: r.on ? r.ac : T.subtle,
              boxShadow: r.on ? `0 0 5px ${r.ac}` : 'none',
            }} />
            <span style={{ fontSize: '11px', color: r.on ? T.text : T.subtle, flex: 1 }}>{r.label}</span>
            <span style={{ fontSize: '10px', fontWeight: 700, color: r.on ? r.ac : T.subtle }}>
              {r.on ? 'ON' : 'OFF'}
            </span>
          </div>
        ))}
      </div>

      <SLabel>Pyramiding</SLabel>
      <Box style={{ marginBottom: '10px' }}>
        <div style={{ fontSize: '11px', color: T.muted, lineHeight: '1.6' }}>{pyramidNote()}</div>
      </Box>

      {strat === 'DEBIT_SPREAD' && (
        <>
          <SLabel>Abort Rule</SLabel>
          <Box>
            <div style={{ fontSize: '11px', color: T.text }}>
              If short leg fails → remain long-only. Monitor for leg imbalance.
            </div>
          </Box>
        </>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// MODAL SHELL
// ─────────────────────────────────────────────────────────────────────────────
const OptionsStrategyPanel = ({ show, onHide, position, matchedEval }) => {
  const [tab, setTab] = useState('context');

  if (!show || !position) return null;

  const isClosed    = position.status === 'closed';
  const pnlNum      = parseFloat(position.total_pnl_num || 0);
  const strat       = matchedEval?.eval_agent_story?.chart_analysis?.llm_full_response?.option_strategy;
  const sc          = stratStyle(strat);
  const optType     = (position.option_type || '').toUpperCase();
  const dirColor    = position.direction === 'long' ? T.blue : T.amber;

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onHide}
        style={{
          position: 'fixed', inset: 0, zIndex: 1050,
          backgroundColor: 'rgba(0,0,0,0.7)',
        }}
      />

      {/* Modal panel — right drawer style */}
      <div style={{
        position: 'fixed', top: 0, right: 0, bottom: 0, zIndex: 1051,
        width: '520px', maxWidth: '95vw',
        backgroundColor: T.card,
        borderLeft: `1px solid ${T.border2}`,
        display: 'flex', flexDirection: 'column',
        boxShadow: '-8px 0 32px rgba(0,0,0,0.5)',
      }}>

        {/* ── Header ── */}
        <div style={{
          padding: '14px 18px', borderBottom: `1px solid ${T.border}`,
          backgroundColor: T.surface, flexShrink: 0,
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <div style={{ fontSize: '14px', fontWeight: 700, color: T.text, marginBottom: '4px' }}>
                {position.symbol}
              </div>
              <div style={{ fontSize: '11px', color: T.muted }}>
                {optType && <span style={{ marginRight: '8px' }}>{optType}</span>}
                {position.strike && <span style={{ marginRight: '8px' }}>${position.strike}</span>}
                {position.expiration && <span style={{ marginRight: '8px' }}>exp {position.expiration}</span>}
              </div>
            </div>
            <button
              onClick={onHide}
              style={{
                background: 'none', border: `1px solid ${T.border}`, borderRadius: '4px',
                color: T.muted, fontSize: '16px', cursor: 'pointer', padding: '2px 8px',
                lineHeight: 1,
              }}
            >×</button>
          </div>

          {/* Summary chips */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '10px' }}>
            <Chip
              c={isClosed ? T.slate : T.green}
              bg={isClosed ? T.slateBg : T.greenBg}
              b={isClosed ? T.slateBdr : T.greenBdr}
            >
              {(position.status || '').toUpperCase()}
            </Chip>
            <Chip c={dirColor}>{(position.direction || '').toUpperCase()}</Chip>
            <Chip>{v(position.quantity)} contracts</Chip>
            <Chip
              c={PnLColor(pnlNum)}
              bg={pnlNum >= 0 ? T.greenBg : T.redBg}
              b={pnlNum >= 0 ? T.greenBdr : T.redBdr}
            >
              P&L ${fmtN(position.total_pnl_num)}
            </Chip>
            {strat && (
              <Chip c={sc.c} bg={sc.bg} b={sc.b}>{strat}</Chip>
            )}
          </div>
        </div>

        {/* ── Tab bar ── */}
        <div style={{ display: 'flex', borderBottom: `1px solid ${T.border}`, flexShrink: 0 }}>
          {['context', 'execution', 'management'].map(t => (
            <TabBtn
              key={t}
              label={t.charAt(0).toUpperCase() + t.slice(1)}
              active={tab === t}
              onClick={() => setTab(t)}
            />
          ))}
        </div>

        {/* ── Tab content ── */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '14px 18px' }}>
          {tab === 'context'    && <ContextTab    position={position} matchedEval={matchedEval} />}
          {tab === 'execution'  && <ExecutionTab  position={position} />}
          {tab === 'management' && <ManagementTab matchedEval={matchedEval} />}
        </div>
      </div>
    </>
  );
};

export default OptionsStrategyPanel;